import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, FileText, Search, Loader2, Copy } from 'lucide-react';

const Declarations = () => {
  usePageTitle('Declarations');
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeclaration, setEditingDeclaration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    text: ''
  });

  const fetchDeclarations = async () => {
    try {
      const response = await axios.get(`${API_URL}/declarations`);
      setDeclarations(response.data);
    } catch (error) {
      toast.error('Failed to fetch declarations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeclarations();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      text: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.text) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    
    try {
      if (editingDeclaration) {
        await axios.put(`${API_URL}/declarations/${editingDeclaration.id}`, formData);
        toast.success('Declaration updated successfully');
      } else {
        await axios.post(`${API_URL}/declarations`, formData);
        toast.success('Declaration created successfully');
      }
      
      setDialogOpen(false);
      setEditingDeclaration(null);
      resetForm();
      fetchDeclarations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save declaration');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (declaration) => {
    setEditingDeclaration(declaration);
    setFormData({
      title: declaration.title,
      text: declaration.text
    });
    setDialogOpen(true);
  };

  const handleDuplicate = async (declaration) => {
    try {
      const duplicateData = {
        title: `${declaration.title} (Copy)`,
        text: declaration.text
      };
      
      await axios.post(`${API_URL}/declarations`, duplicateData);
      toast.success('Declaration duplicated successfully');
      fetchDeclarations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate declaration');
    }
  };

  const handleDelete = async (declaration) => {
    setDeleteConfirm(declaration);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const declaration = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/declarations/${declaration.id}`);
      toast.success('Declaration deleted successfully');
      fetchDeclarations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete declaration');
    }
  };

  const filteredDeclarations = declarations.filter(declaration =>
    declaration.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    declaration.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="declarations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Declarations
          </h1>
          <p className="text-slate-500 mt-1">
            Create and manage declaration statements for certificates
          </p>
        </div>
        <Button 
          className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
          onClick={() => {
            setEditingDeclaration(null);
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-declaration-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Declaration
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search declarations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="declaration-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Declarations List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            Declarations ({filteredDeclarations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredDeclarations.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No declarations found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Get started by creating your first declaration'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-[200px]">Title</TableHead>
                  <TableHead className="font-semibold">Declaration Text</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeclarations.map((declaration) => (
                  <TableRow key={declaration.id} className="hover:bg-slate-50/50" data-testid={`declaration-row-${declaration.id}`}>
                    <TableCell className="font-medium">{declaration.title}</TableCell>
                    <TableCell className="text-slate-600">
                      <p className="line-clamp-2">{declaration.text}</p>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`declaration-actions-${declaration.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(declaration)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(declaration)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDelete(declaration)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingDeclaration ? 'Edit Declaration' : 'Create Declaration'}
            </DialogTitle>
            <DialogDescription>
              Create a declaration statement that can be selected when creating a class and printed on certificates.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., First Aid Training Completion"
                data-testid="declaration-title-input"
              />
              <p className="text-xs text-slate-500">
                A short title to easily identify this declaration in dropdowns
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="text">Declaration Text *</Label>
              <Textarea
                id="text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Enter the full declaration text that will appear on the certificate..."
                rows={5}
                data-testid="declaration-text-input"
              />
              <p className="text-xs text-slate-500">
                This text can be printed on certificates for classes that use this declaration
              </p>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="declaration-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingDeclaration ? 'Update Declaration' : 'Create Declaration'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Declaration
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.title}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Declarations;
